import appstore from "../../appStore";
import { OptionalUI } from "../Booking/OptionalParts/OptionalUI";
import { GuestPayUserSetupFlow } from "../GuestPayment/GuestPayUserSetupFlow";
import { PayPalGuestTempPlaceholder, ApplePayPlaceholder, GooglePayPlaceholder, CreditCardPlaceholder } from "../Payment/PaymentEntities";
import { BookingService } from "../../Services/BookingService";
import { UpdateVoiceChannelBookingRequest } from "../../Services/PhoneToCnpContracts";
import { BookingPaymentV1 } from "../../Services/BookingEntities";
import { Dispatch } from "../Dispatch";
import { NoticeType, ToasterType, WellknownErrorMessages } from "./UI/Notifications/NotificationEntities";
import { SetDeviceData } from "../Payment/PaymentHelper";

/** For voice channel bookings we have already verified the phone number through the IVR flow, so we can skip SMS verification and proceed to register the guest user and payment method right away. */
export async function ProceedPaymentAndUpdateBookingWithoutSmsVerificationForVoiceChannelBookings() {

    // exit if payment failed for some reason (e.g. PayPal guest user/payment registration failed). The booking should not be updated in this case.
	if (await DeferredGuestPaymentSetup() == false) {
		Dispatch.PhoneToCnp.UpdateFailed();
        Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.GenericMessage);
		return;
	}

	// Update booking with payment details
	await UpdateBookingWithPayment();
}

/** 
 * Perform any deferred setup activities for Guest Payment flows immediately before creating a booking.
 * For example: PayPal guest user creation and payment registration.
 * Returns false if the setup failed and the booking creation should not proceed.
 */
async function DeferredGuestPaymentSetup(): Promise<boolean> {

	const appState = appstore.getState();

	// user has selected PayPal and we haven't created that yet.
	// Note: We deliberately avoid OptionalUI.PendingPayPalGuest here because it also requires BackingUser to be null,
	// which fails when the user already has a registered card from a previous session (returning guest). In the
	// voice-channel flow, we only need to confirm the user is a guest (not logged-in) and that PayPalTokenised
	// is set — the BackingUser constraint does not apply.
	if (!OptionalUI.LoggedInUser(appState) && appState.GuestPayment.PayPalTokenised && appState.PhoneToCnp.PaymentOption === PayPalGuestTempPlaceholder) {

		// If BackingUser is already set (return user with a saved card), clear it before registering PayPal.
		// ServiceManager adds BackingUser.Auth as the Authorization header on every API call, which causes
		// RegisterPayPalGuest to reject the request because the user already has an account.
		// Clearing it here means the request goes unauthenticated, so the backend treats it as a fresh
		// guest registration. Step_2b_PostSmsVerification_PayPal will update BackingUser
		// with the new account's credentials from the response.
		if (appState.GuestPayment.BackingUser) {
			Dispatch.GuestPayment.ClearTempUser();
		}

		return await GuestPayUserSetupFlow.Step_2b_PostSmsVerification_PayPal();
	}

	// user has authorised Apple Pay but we haven't created a user account and payment method yet.
	// Note: We deliberately avoid OptionalUI.PendingApplePayGuest here because it also requires BackingUser to be null,
	// which fails when the user already has a registered card from a previous session (returning guest). In the
	// voice-channel flow, we only need to confirm the user is a guest (not logged-in) and that ApplePayTokenised
	// is set — the BackingUser constraint does not apply.
	if (!OptionalUI.LoggedInUser(appState) && appState.GuestPayment.ApplePayTokenised && appState.PhoneToCnp.PaymentOption === ApplePayPlaceholder) {

		// If BackingUser is already set (return user with a saved card), clear it before registering Apple Pay.
		// ServiceManager adds BackingUser.Auth as the Authorization header on every API call, which causes
		// RegisterApplePayGuest to reject the request because the user already has an account.
		// Clearing it here means the request goes unauthenticated, so the backend treats it as a fresh
		// guest registration. PostSmsVerification_ApplePay will update BackingUser
		// with the new account's credentials from the response.
		if (appState.GuestPayment.BackingUser) {
			Dispatch.GuestPayment.ClearTempUser();
		}

		return await GuestPayUserSetupFlow.PostSmsVerification_ApplePay();
	}

	// user has authorised Google Pay but we haven't created a user account and payment method yet.
	// Note: We deliberately avoid OptionalUI.PendingGooglePayGuest here because it also requires BackingUser to be null,
	// which fails when the user already has a registered card from a previous session (returning guest). In the
	// voice-channel flow, we only need to confirm the user is a guest (not logged-in) and that GooglePayTokenised
	// is set — the BackingUser constraint does not apply.
	if (!OptionalUI.LoggedInUser(appState) && appState.GuestPayment.GooglePayTokenised && appState.PhoneToCnp.PaymentOption === GooglePayPlaceholder) {

		// If BackingUser is already set (return user with a saved card), clear it before registering Google Pay.
		// ServiceManager adds BackingUser.Auth as the Authorization header on every API call, which causes
		// RegisterGooglePayGuest to reject the request because the user already has an account.
		// Clearing it here means the request goes unauthenticated, so the backend treats it as a fresh
		// guest registration. PostSmsVerification_GooglePay will update BackingUser
		// with the new account's credentials from the response.
		if (appState.GuestPayment.BackingUser) {
			Dispatch.GuestPayment.ClearTempUser();
		}

		return await GuestPayUserSetupFlow.PostSmsVerification_GooglePay();
	}

	// user has tokenised and verified a credit/debit card but we haven't registered the payment method yet.
	// Note: We deliberately avoid OptionalUI.PendingCreditCardGuest here because it also requires BackingUser to be null,
	// which fails when the user already has a registered card from a previous session (returning guest). In the
	// voice-channel flow, we only need to confirm the user is a guest (not logged-in) and that CreditCard3DsVerified
	// is set — the BackingUser constraint does not apply.
	if (!OptionalUI.LoggedInUser(appState) && appState.GuestPayment.CreditCard3DsVerified && appState.PhoneToCnp.PaymentOption === CreditCardPlaceholder) {

		// If BackingUser is already set (return user with a saved card), clear it before registering the new card.
		// ServiceManager adds BackingUser.Auth as the Authorization header on every API call, which causes
		// RegisterCreditCardGuest to reject the request because the user already has an account.
		// Clearing it here means the request goes unauthenticated, so the backend treats it as a fresh
		// guest registration for the new card. PostSmsVerification_CreditCard will update BackingUser
		// with the new account's credentials from the response.
		if (appState.GuestPayment.BackingUser) {
			Dispatch.GuestPayment.ClearTempUser();
		}

		return await GuestPayUserSetupFlow.PostSmsVerification_CreditCard();
	}

	// no action required
	return true;
}

/**
 * Update the voice channel booking with payment details to convert it to a fixed price CNP booking.
 */
async function UpdateBookingWithPayment(): Promise<void> {
	
	const appState = appstore.getState();
	const booking = appState.PhoneToCnp.Booking!;
	const paymentOption = appState.PhoneToCnp.PaymentOption;
	let deviceData = appState.booking.DeviceData;
	const saveCard = appState.PhoneToCnp.SaveCardForFutureTrips;
	const receiptEmail = appState.GuestPayment.EmailForReceipt;
	const threeDsVerified = appState.GuestPayment.CreditCard3DsVerified;

	let paymentCardId = null;

	// book with Apple Pay
	if (paymentOption?.Type == "ApplePay" && appState.payment.ApplePay) paymentCardId = appState.payment.ApplePay.CardId;

	// book with Google Pay
	if (paymentOption?.Type == "GooglePay" && appState.payment.GooglePay) paymentCardId = appState.payment.GooglePay.CardId;

	// book with a credit/debit card as a guest
	if (paymentOption === CreditCardPlaceholder && appState.payment.GuestCreditCard) paymentCardId = appState.payment.GuestCreditCard.CardId;

	// book with PayPal
	if (paymentOption === PayPalGuestTempPlaceholder && appState.payment.PayPalGuest) paymentCardId = appState.payment.PayPalGuest.CardId;

	// book with an existing registered card (e.g. from booking.Payment or booking.SavedCard for return users).
	// The card is already registered in MPS so we just pass its CardId — no guest payment setup required.
	if (paymentCardId === null && paymentOption?.Card?.CardId) paymentCardId = paymentOption.Card.CardId;

	// Populate Braintree device data for fraud detection if not already set.
	// For the saved card flow, Braintree hasn't been initialized yet so DeviceData is null.
	// SetDeviceData() initializes a Braintree client and collects device data, then dispatches it to the store.
	if (!deviceData) {
		try {
			await SetDeviceData();
			deviceData = appstore.getState().booking.DeviceData;
		} catch (e) {
			// Device data collection failed — proceed with empty string.
			// This is a known limitation for the saved card flow if Braintree isn't ready.
			console.warn('[UpdateBookingWithPayment] Failed to collect Braintree device data:', e);
		}
	}
	
	// Build the payment details
	const payment: BookingPaymentV1 = {
		DeviceData: deviceData ?? '', // Fall back to empty string if Braintree device data collection failed
		PaymentCardId: paymentCardId,
		Fare: null,
		AccountInfo: null,
		PaymentPreAuthOptIn: true, // Enable pre-auth for CNP bookings
		IsFixedFare: true, // Converting to fixed fare
		Upgraded3DsNonce: threeDsVerified?.nonce ?? null,
		LiabilityShifted3Ds: threeDsVerified?.liabilityShifted ?? null,
		ReceiptEmail: receiptEmail ?? null
	};
	
	// Build the request
	const request: UpdateVoiceChannelBookingRequest = {
		Payment: payment,
		BookingKey: {
			bookingId: booking!.BookingId,
			hashCode: booking!.AccessCode
		},
		SavePaymentCard: saveCard
	};
	
	// Call the API
	const result = await BookingService.UpdateVoiceChannelBooking(request);
	
	if (result.isSuccess) {
		Dispatch.PhoneToCnp.ConfirmPayment();
        Dispatch.PhoneToCnp.UpdateSucceeded();
		Dispatch.PhoneToCnp.ShowToaster(ToasterType.PaymentMethodSaved);

		if (appState.PhoneToCnp.NoticeToDisplay === NoticeType.FixedPriceNotLockedIn) {
			Dispatch.PhoneToCnp.HideNotice();
		}

	} else {
        Dispatch.PhoneToCnp.UpdateFailed();
		Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.GenericMessage);
	}
}
